import {
  DatePickerProps as AntDatePickerProps,
  DatePicker as AntDatePicker,
} from 'antd'
import { RangePickerProps as AntRangePickerProps } from 'antd/lib/date-picker'
import moment from 'moment'
import { Moment } from 'moment-timezone'
import styled from 'styled-components'

import { baseInputStyles } from './Input'

import { useBreakpoint } from '@/hooks/useBreakpoint'
import { findScrollableParent } from '@/utils/findScrollableParent'

const PresetWrapper = styled.div`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`

const Preset = styled.button`
  ${({ theme }) => theme.font.caption01}
  border: 0;
  padding: 0.125rem 0.75rem;
  background-color: ${({ theme }) => theme.colour.secondary._50};
  color: ${({ theme }) => theme.colour.secondary._700};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colour.neutral.surface};
  }
`

const Divider = styled.div`
  width: 0.5rem;
`

const BuildPassLookinDatePicker = styled(AntDatePicker)<DatePickerProps>`
  ${baseInputStyles}
`

export type DatePickerProps = AntDatePickerProps & {
  children?: React.ReactNode
}

export const DatePicker: React.FC<DatePickerProps> = ({
  children,
  inputReadOnly,
  ...datePickerProps
}) => {
  const isMobile = useBreakpoint('sm')

  return (
    <BuildPassLookinDatePicker
      getPopupContainer={findScrollableParent}
      inputReadOnly={isMobile ? true : inputReadOnly}
      {...datePickerProps}
    >
      {children}
    </BuildPassLookinDatePicker>
  )
}

const BuildPassLookinRangePicker = styled(AntDatePicker.RangePicker)`
  ${baseInputStyles}
`
export type RangePickerProps = AntRangePickerProps & {
  children?: React.ReactNode
}

export const RangePicker: React.FC<RangePickerProps> = ({
  inputReadOnly,
  ...rangePickerProps
}) => {
  const isMobile = useBreakpoint('sm')

  return (
    <BuildPassLookinRangePicker
      getPopupContainer={findScrollableParent}
      inputReadOnly={isMobile ? true : inputReadOnly}
      {...rangePickerProps}
    />
  )
}

DatePicker.displayName = 'DatePicker'
RangePicker.displayName = 'RangePicker'

type CustomDateRangePresetsProps = {
  onChange: (dates: [Moment, Moment]) => void
}

export const CustomDateRangePresets: React.FC<CustomDateRangePresetsProps> = ({
  onChange,
}) => {
  const setTime = (unit: moment.unitOfTime.Base, shift = 0) => {
    const today = moment()
    const startOfX = today.clone().startOf(unit).add(shift, unit)
    const endOfX = today.clone().endOf(unit).add(shift, unit)
    onChange([startOfX, endOfX])
  }

  const setToday = () => setTime('day')
  const setYesterday = () => setTime('day', -1)

  const setThisWeek = () => setTime('week')
  const setLastWeek = () => setTime('week', -1)

  const setThisMonth = () => setTime('month')
  const setLastMonth = () => setTime('month', -1)

  return (
    <PresetWrapper>
      <Preset onClick={setToday}>Today</Preset>
      <Preset onClick={setYesterday}>Yesterday</Preset>
      <Divider />
      <Preset onClick={setThisWeek}>This week</Preset>
      <Preset onClick={setLastWeek}>Last week</Preset>
      <Divider />
      <Preset onClick={setThisMonth}>This month</Preset>
      <Preset onClick={setLastMonth}>Last month</Preset>
    </PresetWrapper>
  )
}
